import * as THREE from 'three';

import {
  COUNTER_LAPTOP_OFFSET,
  type Fixture,
  SCREEN,
  SCREEN_IMAGE_ASPECT,
  type StationScreen,
  TV,
} from '../config/booth';
import { BRAND } from '../textures/canvas';
import { makeKioskSign, makeTrialCard } from '../textures/panels';

const mats = {
  white: new THREE.MeshStandardMaterial({ color: BRAND.white, roughness: 0.4 }),
  navy: new THREE.MeshStandardMaterial({ color: BRAND.navy, roughness: 0.55 }),
  led: new THREE.MeshStandardMaterial({
    color: BRAND.red,
    emissive: BRAND.red,
    emissiveIntensity: 1.6,
  }),
  key: new THREE.MeshStandardMaterial({ color: '#1a1d26', roughness: 0.7 }),
  stand: new THREE.MeshStandardMaterial({
    color: '#2b2e36',
    roughness: 0.5,
    metalness: 0.6,
  }),
  laptop: new THREE.MeshStandardMaterial({
    color: '#8d9099',
    roughness: 0.45,
    metalness: 0.5,
  }),
};

function box(
  w: number,
  h: number,
  d: number,
  mat: THREE.Material,
  x: number,
  y: number,
  z: number,
): THREE.Mesh {
  const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat);
  m.position.set(x, y, z);
  m.castShadow = true;
  m.receiveShadow = true;
  return m;
}

function logoDecal(logo: THREE.Texture, w: number): THREE.Mesh {
  const m = new THREE.Mesh(
    new THREE.PlaneGeometry(w, w * (437 / 3000)),
    new THREE.MeshStandardMaterial({
      map: logo,
      transparent: true,
      roughness: 0.5,
    }),
  );
  return m;
}

function counter(
  f: Fixture,
  logo: THREE.Texture,
  appScreen: THREE.Texture,
): THREE.Group {
  const g = new THREE.Group();
  g.add(box(f.w, f.h - 0.5, f.d, mats.white, 0, (f.h - 0.5) / 2 + 0.3, 0));
  g.add(box(f.w, 0.3, f.d, mats.navy, 0, 0.15, 0));
  g.add(box(f.w + 0.1, 0.12, f.d + 0.1, mats.navy, 0, f.h - 0.06, 0));
  g.add(box(f.w, 0.06, 0.06, mats.led, 0, 0.33, f.d / 2 + 0.03));
  const decal = logoDecal(logo, f.w * 0.7);
  decal.position.set(0, f.h / 2 + 0.1, f.d / 2 + 0.02);
  g.add(decal);
  // Trial card on the right; a third demo laptop on the left, where the
  // rendering had a plant. The counter is a demo station in the sim too.
  const card = new THREE.Mesh(
    new THREE.PlaneGeometry(0.9, 1.14),
    new THREE.MeshStandardMaterial({ map: makeTrialCard(), roughness: 0.6 }),
  );
  card.position.set(f.w * 0.3, f.h + 0.55, 0);
  card.rotation.x = -0.25;
  g.add(card);
  const lap = laptop(appScreen);
  lap.position.set(-f.w * COUNTER_LAPTOP_OFFSET, f.h, 0.15);
  g.add(lap);
  return g;
}

/** A 15" laptop, lid open about 105°, showing the app. Dimensions in feet. */
function laptop(appScreen: THREE.Texture): THREE.Group {
  const g = new THREE.Group();
  const w = 1.15;
  const d = 0.8;
  const base = box(w, 0.05, d, mats.laptop, 0, 0.025, 0);
  g.add(base);
  const keys = box(w * 0.86, 0.012, d * 0.5, mats.key, 0, 0.055, -d * 0.12);
  g.add(keys);
  const pad = box(w * 0.32, 0.008, d * 0.26, mats.key, 0, 0.055, d * 0.3);
  g.add(pad);
  const lid = new THREE.Group();
  lid.position.set(0, 0.05, -d / 2);
  // Hinge at the back edge; the lid leans back ~15° past upright.
  lid.rotation.x = Math.PI / 2 - 0.26;
  const shell = box(w, 0.035, d, mats.laptop, 0, 0, -d / 2);
  lid.add(shell);
  const screen = new THREE.Mesh(
    new THREE.PlaneGeometry(w * 0.92, d * 0.9),
    new THREE.MeshStandardMaterial({
      map: appScreen,
      emissive: '#ffffff',
      emissiveMap: appScreen,
      emissiveIntensity: 0.7,
      roughness: 0.35,
    }),
  );
  screen.rotation.x = -Math.PI / 2;
  screen.position.set(0, 0.025, -d / 2);
  lid.add(screen);
  g.add(lid);
  return g;
}

function kiosk(
  f: Fixture,
  logo: THREE.Texture,
  appScreen: THREE.Texture,
): THREE.Group {
  const g = new THREE.Group();
  const pedestalH = f.h - 0.2;
  g.add(box(f.w * 0.8, pedestalH, f.d * 0.8, mats.navy, 0, pedestalH / 2, 0));
  g.add(box(f.w * 0.8, 0.06, 0.06, mats.led, 0, 0.04, f.d * 0.4 + 0.03));
  const decal = logoDecal(logo, f.w * 0.55);
  decal.position.set(0, pedestalH * 0.5, f.d * 0.4 + 0.02);
  g.add(decal);
  const top = box(f.w, 0.15, f.d, mats.white, 0, pedestalH + 0.075, 0);
  g.add(top);
  const lap = laptop(appScreen);
  lap.position.set(0, pedestalH + 0.15, 0.1);
  g.add(lap);
  // "Try it live" sign rising behind the laptop.
  const sign = new THREE.Mesh(
    new THREE.PlaneGeometry(f.w * 0.9, f.w * 0.9 * 0.5),
    new THREE.MeshStandardMaterial({ map: makeKioskSign(), roughness: 0.55 }),
  );
  sign.position.set(0, pedestalH + 1.0, -f.d * 0.46);
  sign.rotation.x = -0.18;
  sign.userData['sign'] = true;
  g.add(sign);
  const signBack = box(
    f.w * 0.94,
    f.w * 0.9 * 0.5 + 0.06,
    0.06,
    mats.navy,
    0,
    pedestalH + 1.0,
    -f.d * 0.46 - 0.04,
  );
  signBack.rotation.x = -0.18;
  signBack.userData['sign'] = true;
  g.add(signBack);
  return g;
}

/**
 * A 75" panel on a rolling floor stand: base plate, two posts, the TV hung
 * between them with its centre at TV.centreHeight. The capture is fitted to
 * the panel's height with black either side, as a browser window on a TV.
 */
function tvStand(f: Fixture, appScreen: THREE.Texture): THREE.Group {
  const g = new THREE.Group();
  g.add(box(f.w, 0.1, f.d, mats.stand, 0, 0.05, 0));
  const postH = TV.centreHeight + SCREEN.height / 2 - 0.3;
  for (const x of [-0.55, 0.55]) {
    const post = new THREE.Mesh(
      new THREE.CylinderGeometry(0.07, 0.07, postH, 12),
      mats.stand,
    );
    post.position.set(x, postH / 2 + 0.1, -0.2);
    post.castShadow = true;
    g.add(post);
  }
  g.add(box(1.4, 0.08, 0.08, mats.stand, 0, TV.centreHeight, -0.2));
  g.add(
    box(
      SCREEN.width + 0.1,
      SCREEN.height + 0.1,
      0.12,
      mats.key,
      0,
      TV.centreHeight,
      0,
    ),
  );
  const glass = new THREE.Mesh(
    new THREE.PlaneGeometry(SCREEN.width, SCREEN.height),
    new THREE.MeshStandardMaterial({ color: '#05070d', roughness: 0.2 }),
  );
  glass.position.set(0, TV.centreHeight, 0.065);
  g.add(glass);
  const image = new THREE.Mesh(
    new THREE.PlaneGeometry(SCREEN.height * SCREEN_IMAGE_ASPECT, SCREEN.height),
    new THREE.MeshStandardMaterial({
      map: appScreen,
      emissive: '#ffffff',
      emissiveMap: appScreen,
      emissiveIntensity: 0.9,
      roughness: 0.35,
    }),
  );
  image.position.set(0, TV.centreHeight, 0.07);
  g.add(image);
  return g;
}

function closet(f: Fixture): THREE.Group {
  const g = new THREE.Group();
  g.add(box(f.w, f.h, f.d, mats.white, 0, f.h / 2, 0));
  const door = new THREE.Mesh(
    new THREE.PlaneGeometry(f.w * 0.7, f.h * 0.85),
    new THREE.MeshStandardMaterial({ color: '#e4e6ee', roughness: 0.6 }),
  );
  door.rotation.y = -Math.PI / 2;
  door.position.set(-f.w / 2 - 0.01, f.h * 0.44, 0);
  g.add(door);
  const handle = box(
    0.04,
    0.3,
    0.06,
    mats.navy,
    -f.w / 2 - 0.04,
    f.h * 0.44,
    f.d * 0.25,
  );
  g.add(handle);
  return g;
}

export interface FixtureSet {
  group: THREE.Group;
  /** id → draggable root; the root's userData.id names the fixture. */
  roots: Map<string, THREE.Group>;
  place(f: Fixture): void;
  /** Shows or hides the "Try it live" signs on the pedestals. */
  setSignsVisible(on: boolean): void;
}

/** One texture per app page a laptop can be open to. */
export type ScreenTextures = Record<StationScreen, THREE.Texture>;

export function buildFixtures(
  fixtures: Fixture[],
  logo: THREE.Texture,
  screens: ScreenTextures,
): FixtureSet {
  const group = new THREE.Group();
  const roots = new Map<string, THREE.Group>();
  for (const f of fixtures) {
    const screen = screens[f.screen ?? 'search'];
    const root =
      f.kind === 'counter'
        ? counter(f, logo, screen)
        : f.kind === 'kiosk'
          ? kiosk(f, logo, screen)
          : f.kind === 'tv'
            ? tvStand(f, screen)
            : closet(f);
    root.userData['id'] = f.id;
    root.userData['kind'] = 'fixture';
    root.position.set(f.x, 0, f.z);
    root.rotation.y = f.rot;
    roots.set(f.id, root);
    group.add(root);
  }
  return {
    group,
    roots,
    place(f) {
      const root = roots.get(f.id);
      if (!root) return;
      root.position.set(f.x, 0, f.z);
      root.rotation.y = f.rot;
    },
    setSignsVisible(on) {
      group.traverse((o) => {
        if (o.userData['sign']) o.visible = on;
      });
    },
  };
}
